import { useEffect, useRef, useState } from 'react';
import { fetchEquipmentFile } from '../api';
import { platform } from '../platform';
import { useApp } from '../state';
import { Icon } from '../ui';

/**
 * Просмотр PDF внутри приложения: страницы рисуются картинками (PDF.js), файл приходит с авторизацией
 * и наружу не уходит — ни ссылки, ни кнопки «скачать». PDF.js лежит в public/pdfjs и грузится только здесь.
 */

interface PdfPage {
  getViewport(o: { scale: number }): { width: number; height: number };
  render(o: { canvasContext: CanvasRenderingContext2D; viewport: { width: number; height: number }; transform?: number[] }): { promise: Promise<void>; cancel(): void };
  cleanup(): void;
}
interface PdfDoc {
  numPages: number;
  getPage(n: number): Promise<PdfPage>;
}
interface PdfLib {
  GlobalWorkerOptions: { workerSrc: string };
  getDocument(o: { data: Uint8Array; isEvalSupported: boolean; enableXfa: boolean }): { promise: Promise<PdfDoc>; destroy(): Promise<void> };
}

let libPromise: Promise<PdfLib> | null = null;
function loadPdfJs(): Promise<PdfLib> {
  libPromise ??= (import(/* @vite-ignore */ `${location.origin}/pdfjs/pdf.min.mjs`) as Promise<PdfLib>).then((lib) => {
    lib.GlobalWorkerOptions.workerSrc = `${location.origin}/pdfjs/pdf.worker.min.mjs`;
    return lib;
  });
  return libPromise;
}

const ZOOMS = [1, 1.5, 2, 3];

export function PdfViewer({ equipmentId, fileId, title, onClose }: { equipmentId: string; fileId: string; title: string; onClose: () => void }) {
  const { close } = useApp();
  const pagesRef = useRef<HTMLDivElement>(null);
  const [doc, setDoc] = useState<PdfDoc | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(0);

  // Системная кнопка «Назад» Telegram закрывает документ, а не страницу оборудования.
  useEffect(() => {
    platform.setBack(onClose);
    return () => platform.setBack(close);
  }, [onClose, close]);

  useEffect(() => {
    let alive = true;
    let task: { destroy(): Promise<void> } | null = null;
    const release = () => {
      try {
        void task?.destroy().catch(() => {});
      } catch {
        /* уже освобождён */
      }
    };
    (async () => {
      try {
        const [lib, bytes] = await Promise.all([loadPdfJs(), fetchEquipmentFile(equipmentId, fileId)]);
        if (!alive) return;
        const t = lib.getDocument({ data: new Uint8Array(bytes), isEvalSupported: false, enableXfa: false });
        task = t;
        const loaded = await t.promise;
        if (alive) setDoc(loaded);
        else release();
      } catch (e) {
        if (alive) setError(e instanceof Error && e.message ? e.message : 'Документ не открылся');
      }
    })();
    return () => {
      alive = false;
      release();
    };
  }, [equipmentId, fileId]);

  // Рисуем страницы под ширину экрана (и выбранный масштаб); при смене масштаба — заново.
  useEffect(() => {
    const host = pagesRef.current;
    if (!doc || !host) return;
    let cancelled = false;
    const tasks: { cancel(): void }[] = [];
    host.replaceChildren();
    const width = Math.max(200, (host.parentElement?.clientWidth ?? window.innerWidth) - 16) * ZOOMS[zoom]!;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    (async () => {
      for (let n = 1; n <= doc.numPages && !cancelled; n++) {
        const page = await doc.getPage(n);
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: width / base.width });
        const canvas = document.createElement('canvas');
        // Предел размера холста на iPhone ~16 млн точек — при большом масштабе снижаем чёткость, а не падаем.
        const k = Math.min(dpr, Math.sqrt(16_000_000 / (viewport.width * viewport.height)));
        canvas.width = Math.floor(viewport.width * k);
        canvas.height = Math.floor(viewport.height * k);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        canvas.setAttribute('aria-label', `Страница ${n} из ${doc.numPages}`);
        host.appendChild(canvas);
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        const task = page.render({ canvasContext: ctx, viewport, transform: [k, 0, 0, k, 0, 0] });
        tasks.push(task);
        await task.promise.catch(() => {});
        page.cleanup();
      }
    })();
    return () => {
      cancelled = true;
      tasks.forEach((t) => {
        try {
          t.cancel();
        } catch {
          /* уже нарисована */
        }
      });
    };
  }, [doc, zoom]);

  return (
    <div className="docview" role="dialog" aria-label={title}>
      <div className="docview-bar">
        <button type="button" className="icon-btn" aria-label="Закрыть документ" onClick={onClose}>
          <Icon name="back" size={20} />
        </button>
        <div className="viewer-title">
          <b>{title}</b>
          <span>{doc ? `${doc.numPages} стр. · масштаб ${Math.round(ZOOMS[zoom]! * 100)}%` : error ? 'ошибка' : 'открываю…'}</span>
        </div>
        <button type="button" className="icon-btn" aria-label="Мельче" disabled={zoom === 0} onClick={() => setZoom((z) => Math.max(0, z - 1))}>
          <Icon name="shrink" size={18} />
        </button>
        <button type="button" className="icon-btn" aria-label="Крупнее" disabled={zoom === ZOOMS.length - 1} onClick={() => setZoom((z) => Math.min(ZOOMS.length - 1, z + 1))}>
          <Icon name="expand" size={18} />
        </button>
      </div>
      <div className="docview-body" onContextMenu={(e) => e.preventDefault()}>
        {!doc && !error && (
          <div className="viewer-overlay">
            <div className="spinner" aria-hidden="true" />
            <p>Открываю документ…</p>
          </div>
        )}
        {error && (
          <div className="viewer-overlay">
            <Icon name="info" size={32} />
            <p>{error}</p>
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Закрыть
            </button>
          </div>
        )}
        <div ref={pagesRef} className="docview-pages" />
      </div>
    </div>
  );
}
